import React, { useEffect, useState, useCallback } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'mhc_cookie_consent_v1';

// Applies the user's choice to Google Analytics (Consent Mode v2) and PostHog.
const applyConsent = (status) => {
  const granted = status === 'granted';
  if (typeof window === 'undefined') return;

  // Google Consent Mode v2 update
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied',
      functionality_storage: granted ? 'granted' : 'denied',
      personalization_storage: granted ? 'granted' : 'denied',
    });
  }

  // PostHog opt-in / opt-out
  if (window.posthog && typeof window.posthog.opt_in_capturing === 'function') {
    if (granted) {
      window.posthog.opt_in_capturing();
      if (typeof window.posthog.startSessionRecording === 'function') {
        window.posthog.startSessionRecording();
      }
    } else {
      window.posthog.opt_out_capturing();
      if (typeof window.posthog.stopSessionRecording === 'function') {
        window.posthog.stopSessionRecording();
      }
    }
  }
};

export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent('mhc:open-cookie-preferences'));
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show banner if no prior choice
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setVisible(true);
      }
    } catch (e) {
      setVisible(true);
    }

    const openHandler = () => setVisible(true);
    window.addEventListener('mhc:open-cookie-preferences', openHandler);
    return () => window.removeEventListener('mhc:open-cookie-preferences', openHandler);
  }, []);

  const persist = useCallback((status) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ status, timestamp: new Date().toISOString() })
      );
    } catch (e) {}
    applyConsent(status);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 right-4 z-[9998] w-[calc(100%-2rem)] max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="relative bg-card border border-border shadow-xl rounded-xl p-4">
        <button
          onClick={() => persist('denied')}
          aria-label="Close and decline cookies"
          data-testid="cookie-consent-close"
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Cookie className="w-4 h-4 text-primary" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-sm font-semibold text-foreground mb-1">
              We value your privacy
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed">
              We use cookies to analyze traffic and improve your experience.
              Manage anytime from the footer.
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => persist('denied')}
            data-testid="cookie-consent-decline"
            className="flex-1 text-xs"
          >
            Decline
          </Button>
          <Button
            variant="hero"
            size="sm"
            onClick={() => persist('granted')}
            data-testid="cookie-consent-accept"
            className="flex-1 text-xs"
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
